import React from 'react'
import { Link } from 'gatsby'

import {
  FooterProps,
  FooterLink,
  FooterColumn,
  Icon,
} from 'src/interfaces/Footer'
import EmailCapture from './global/emailCapture'
import { NewLogo } from './svgs'

const date = new Date()

const defaultFooterColumns: FooterColumn[] = [
  {
    title: 'Services',
    links: [
      { title: 'Primary Care', link: '/services/primary-care' },
      { title: 'Gynecology', link: '/services/gynecology' },
      { title: 'Mental Health', link: '/services/mental-health' },
      { title: 'Skin Care', link: '/services/skin-care' },
      { title: 'All Services', link: '/services' },
    ],
  },
  {
    title: 'Locations',
    links: [
      { title: 'Los Angeles', link: '/locations/tia-la' },
      { title: 'San Francisco', link: '/locations/tia-san-francisco' },
      { title: 'New York', link: '/locations/ny' },
      { title: 'Arizona', link: '/locations/az' },
    ],
  },
  {
    title: 'Company',
    links: [
      { title: 'About Us', link: '/company' },
      { title: 'Contact Us', link: '/contact' },
      { title: 'Careers', link: '/careers' },
    ],
  },
  {
    title: 'Resources',
    links: [
      { title: 'Insurance', link: '/insurance' },
      { title: 'Service List Prices', link: '/tia-service-list' },
      { title: 'Blog', link: '/tiatake' },
      { title: 'Press Center', link: '/press' },
      { title: 'FAQs', link: 'https://intercom.help/ask-tia/en/' },
    ],
  },
]

const footerEmailCaptureData = {
  _type: 'emailCapture' as const,
  title: 'Subscribe to our newsletter',
  subTitle: '',
  emailCaptureLocation: 'footer',
}

const isExternalLink = (link: string) =>
  link.indexOf('https://') === 0 || link.indexOf('http://') === 0

const normalizeInternalLink = (link: string) => {
  if (!link) return '/'
  if (link === '#') return link
  if (isExternalLink(link)) return link

  const normalizedLink = link.indexOf('/') === 0 ? link : `/${link}`

  if (normalizedLink.includes('?') || normalizedLink.includes('#')) {
    return normalizedLink
  }

  return normalizedLink.endsWith('/') ? normalizedLink : `${normalizedLink}/`
}

const getHostname = (link: string) => {
  if (!isExternalLink(link)) return ''
  try {
    return new URL(link).hostname.toLowerCase()
  } catch {
    return ''
  }
}

const getSocialLabel = (link: string) => {
  const host = getHostname(link)

  if (link.includes('instagram')) return 'Instagram'
  if (
    host === 'twitter.com' ||
    host.endsWith('.twitter.com') ||
    host === 'x.com' ||
    host.endsWith('.x.com') ||
    link.includes('twitter')
  )
    return 'Twitter'
  if (link.includes('facebook')) return 'Facebook'
  if (link.includes('linkedin')) return 'LinkedIn'
  if (link.includes('youtube')) return 'YouTube'

  return 'Social link'
}

const FooterLinkItem = ({
  link,
  title,
  className = 'footer__link',
}: FooterLink & { className?: string }) => {
  const normalizedLink = normalizeInternalLink(link)

  if (isExternalLink(normalizedLink)) {
    return (
      <a
        className={className}
        href={normalizedLink}
        target="_blank"
        rel="noopener nofollow"
      >
        {title}
      </a>
    )
  }

  return (
    <Link className={className} to={normalizedLink}>
      {title}
    </Link>
  )
}

const SocialLinks = ({ icons }: { icons: Icon[] }) => {
  if (!icons?.length) return null

  return (
    <div className="footer__social" aria-label="Social links">
      {icons.map((icon: Icon) => (
        <a
          key={icon._key}
          href={icon.link}
          rel="noopener nofollow"
          target="_blank"
          aria-label={getSocialLabel(icon.link)}
        >
          <img
            width="20"
            height="20"
            src={icon.footerIcon.asset.url}
            alt=""
          />
        </a>
      ))}
    </div>
  )
}

const LegalLinks = ({ links }: { links: FooterLink[] }) => (
  <div className="footer__legal-links">
    {links?.map((bottomLink: FooterLink) => (
      <FooterLinkItem
        key={bottomLink._key || `${bottomLink.title}-${bottomLink.link}`}
        className="footer__legal-link"
        title={bottomLink.title}
        link={bottomLink.link}
      />
    ))}
  </div>
)

const getFooterColumns = (footerColumns?: FooterColumn[]) => {
  const editableColumns = footerColumns?.filter(
    (column: FooterColumn) => column?.title && column.links?.length
  )

  return editableColumns?.length ? editableColumns : defaultFooterColumns
}

const BrandColumn = ({ icons }: { icons: Icon[] }) => (
  <div className="footer__brand">
    <Link aria-label="navigate to homepage" className="footer__logo" to="/">
      <NewLogo className="block" heightOverride="30px" />
    </Link>
    <p className="footer__brand-copy">
      Whole-person healthcare designed around you.
    </p>
    <SocialLinks icons={icons} />
  </div>
)

const AbbreviatedFooter = (props: { footerProps: FooterProps }) => (
  <footer className="footer footer--abbreviated rel z3">
    <div className="footer__inner container--l mxa outer">
      <BrandColumn icons={props.footerProps.icons} />
      <div className="footer__bottom">
        <LegalLinks links={props.footerProps.bottomLinks} />
        <div className="footer__copy">&copy; Tia {date.getFullYear()}</div>
      </div>
    </div>
  </footer>
)

const FullFooter = (props: { footerProps: FooterProps }) => (
  <footer className="footer rel z3">
    <div className="footer__inner container--l mxa outer">
      <div className="footer__top">
        <BrandColumn icons={props.footerProps.icons} />
        <nav className="footer__nav" aria-label="Footer navigation">
          {getFooterColumns(props.footerProps.footerColumns).map(
            (column: FooterColumn) => (
              <div
                className="footer__nav-column"
                key={column._key || column.title}
              >
                <h2 className="footer__nav-title">{column.title}</h2>
                <div className="footer__nav-links">
                  {column.links.map((link: FooterLink) => (
                    <FooterLinkItem
                      key={link._key || `${column.title}-${link.title}`}
                      {...link}
                    />
                  ))}
                </div>
              </div>
            )
          )}
        </nav>
        <div className="footer__newsletter">
          <EmailCapture data={footerEmailCaptureData} variant="footer" />
        </div>
      </div>
      <div className="footer__bottom">
        <LegalLinks links={props.footerProps.bottomLinks} />
        <div className="footer__copy">&copy; Tia {date.getFullYear()}</div>
      </div>
    </div>
  </footer>
)

export const Footer = (props: {
  footerProps: FooterProps
  abbreviated: boolean
}) => {
  if (props.abbreviated) {
    return <AbbreviatedFooter footerProps={props.footerProps} />
  }

  return <FullFooter footerProps={props.footerProps} />
}
